import React, { useCallback, useEffect, useState } from "react";
import { CPDropdown } from "../../CPDropdown";
import { CPInput } from "../../CPInput";
import { useConfigurationCatalogs } from "../../../hooks/useConfigurationCatalogs";
import {
  filterPayrollRatesForLookup,
  isUserRatePayrollType,
  listPayrollRates,
  saveCustomEmployeeRate,
  savePayrollRateMatrix,
  toAdminMatrixPayrollTypeOptions,
  USER_RATE_PAYROLL_TYPE,
} from "../../../helpers/admin/payrollRatesAdmin";
import { listAllUsers } from "../../../helpers/admin/usersAdmin";
import {
  listActiveTrades,
  tradesToDropdownOptions,
} from "../../../helpers/catalogs/tradesCatalog";
import { formatPayrollTypeLabel } from "../../../helpers/payrollHelpers";
import { usePermissions } from "../../../hooks/usePermissions";
import { AdminFormCard, AdminLegacyHeader } from "../shared/AdminFormCard";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { DeskTable } from "../../desktop/DeskTable";
import "../adminDesktop.css";

export function AdminPayrollSuccessScreen({
  editPayrollType,
  editCustomEmployeeId,
  editClassification,
  allUsers,
  onBackToPayroll,
}) {
  const isUserRate = isUserRatePayrollType(editPayrollType);
  const employeeName =
    allUsers.find((u) => u.supabase_user_id === editCustomEmployeeId)
      ?.employee_name || "employee";

  return (
    <div className="screen nx-screen ci-success-screen">
      <div className="ci-success-content">
        <div className="ci-success-icon">
          <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle
              className="checkmark-circle nx-checkmark-circle"
              cx="26"
              cy="26"
              r="24"
            />
            <path
              className="checkmark-check nx-checkmark-check"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
        </div>

        <h1 className="ci-success-title">Payroll Rate Saved</h1>

        <p className="ci-success-sub">
          {isUserRate
            ? `User Rate saved for ${employeeName}.`
            : `${editClassification} · ${formatPayrollTypeLabel(editPayrollType)} has been updated.`}
        </p>

        <button
          type="button"
          className="nx-btn nx-btn-gold"
          style={{ marginTop: "12px" }}
          onClick={onBackToPayroll}
        >
          Back to Payroll Rates
        </button>
      </div>
    </div>
  );
}

export function AdminPayrollRatesPanel({
  onBack,
  showLegacyHeader = false,
  onSaved,
}) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const { classificationOptions, payrollTypeOptions } = useConfigurationCatalogs();
  const [allUsers, setAllUsers] = useState([]);
  const [payrollRates, setPayrollRates] = useState([]);
  const [trades, setTrades] = useState([]);
  const [editClassification, setEditClassification] = useState("");
  const [editPayrollType, setEditPayrollType] = useState("");
  const [editPayrollTradeId, setEditPayrollTradeId] = useState("");
  const [editAdminHourlyRate, setEditAdminHourlyRate] = useState("");
  const [editCustomEmployeeId, setEditCustomEmployeeId] = useState("");
  const [editUserRate, setEditUserRate] = useState("");
  const [payrollLookupClass, setPayrollLookupClass] = useState("");
  const [payrollLookupTrade, setPayrollLookupTrade] = useState("");

  const tradeOptions = tradesToDropdownOptions(trades);
  const matrixPayrollTypeOptions = toAdminMatrixPayrollTypeOptions(
    payrollTypeOptions,
  );

  const loadPayrollRates = useCallback(async () => {
    if (!actor) {
      return;
    }

    try {
      const data = await listPayrollRates(actor);
      setPayrollRates(data);
    } catch (err) {
      console.error("LOAD PAYROLL RATES ERROR:", err);
    }
  }, [actor]);

  const loadTrades = useCallback(async () => {
    if (!actor) {
      return;
    }

    try {
      const data = await listActiveTrades(actor);
      setTrades(data);
    } catch (err) {
      console.error("LOAD TRADES ERROR:", err);
    }
  }, [actor]);

  const loadAllUsers = useCallback(async () => {
    if (!actor) {
      return;
    }
    try {
      const data = await listAllUsers(actor);
      setAllUsers(data);
    } catch (err) {
      console.error("LOAD USERS ERROR:", err);
    }
  }, [actor]);

  useEffect(() => {
    loadPayrollRates();
    loadTrades();
    loadAllUsers();
  }, [loadPayrollRates, loadTrades, loadAllUsers]);

  const handleSaveUserRate = async () => {
    try {
      await saveCustomEmployeeRate({
        supabase_user_id: editCustomEmployeeId,
        hourly_rate: editUserRate,
        organization_id: actor?.organization_id || null,
      });
      const refreshedUsers = await listAllUsers(actor);
      setAllUsers(refreshedUsers);
      onSaved?.({
        editPayrollType: USER_RATE_PAYROLL_TYPE,
        editCustomEmployeeId,
        editClassification: "",
        allUsers: refreshedUsers,
      });
    } catch (err) {
      console.error("SAVE USER RATE ERROR:", err);
      alert(err.message || "Failed to save User Rate");
    }
  };

  const handleSavePayrollRate = async () => {
    try {
      if (!editPayrollType || isUserRatePayrollType(editPayrollType)) {
        alert("Select a matrix payroll type (Residential, Commercial, or Industrial).");
        return;
      }

      await savePayrollRateMatrix(
        {
          trade_id: editPayrollTradeId,
          classification_name: editClassification,
          payroll_type: editPayrollType,
          hourly_rate: editAdminHourlyRate,
        },
        actor,
      );

      await loadPayrollRates();
      onSaved?.({
        editPayrollType,
        editCustomEmployeeId: "",
        editClassification,
        allUsers,
      });
    } catch (err) {
      console.error("SAVE PAYROLL RATE ERROR:", err);
      alert(err.message || "Failed to save payroll rate");
    }
  };

  const lookupRates = filterPayrollRatesForLookup(
    payrollRates,
    payrollLookupTrade,
    payrollLookupClass,
  );

  const content = (
    <div className="adm-split">
      <AdminFormCard title="USER RATE">
        <p className="profile-value" style={{ marginBottom: "12px" }}>
          Personal hourly rate for the selected employee. User Rate is not part of
          the payroll matrix — Start Session uses this value when payroll type is
          User Rate.
        </p>

        <div className="adm-fields">
        <CPDropdown
          label="Employee"
          value={editCustomEmployeeId}
          onChange={(id) => {
            setEditCustomEmployeeId(id);
            const user = allUsers.find((u) => u.supabase_user_id === id);
            setEditUserRate(user?.hourly_rate ?? "");
          }}
          options={[
            { value: "", label: "Select Employee" },
            ...allUsers.map((u) => ({
              value: u.supabase_user_id,
              label: u.employee_name,
            })),
          ]}
        />

        <CPInput
          label="User Rate"
          type="number"
          value={editUserRate}
          onChange={setEditUserRate}
          placeholder="User Rate ($/hr)"
          aria-label="User Rate"
        />
        </div>

        <div className="adm-actions">
        <button type="button" className="kpi-back-btn adm-primary" onClick={handleSaveUserRate}>
          Save User Rate
        </button>
        </div>
      </AdminFormCard>

      <AdminFormCard title="MATRIX RATES">
        <p className="profile-value" style={{ marginBottom: "12px" }}>
          Trade × classification × payroll type. Residential (CCQ) and Residential
          (Non-CCQ) sessions both use the Residential matrix amount. Commercial and
          Industrial use their own rows.
        </p>

        <div className="adm-fields">
        <CPDropdown
          label="Trade"
          value={editPayrollTradeId}
          onChange={setEditPayrollTradeId}
          options={tradeOptions}
        />
        <CPDropdown
          label="Classification"
          value={editClassification}
          onChange={setEditClassification}
          options={classificationOptions}
        />
        <CPDropdown
          label="Payroll Type"
          value={editPayrollType}
          onChange={setEditPayrollType}
          options={matrixPayrollTypeOptions}
        />

        <CPInput
          label="Hourly Rate"
          type="number"
          value={editAdminHourlyRate}
          onChange={setEditAdminHourlyRate}
          placeholder="Hourly Rate"
        />
        </div>

        <div className="adm-actions">
        <button type="button" className="kpi-back-btn adm-primary" onClick={handleSavePayrollRate}>
          Save Matrix Rate
        </button>
        </div>
      </AdminFormCard>

      <AdminFormCard title="VIEW MATRIX RATES" className="adm-span">
        <div className="adm-fields">
        <CPDropdown
          label="Trade"
          value={payrollLookupTrade}
          onChange={(value) => {
            setPayrollLookupTrade(value);
            setPayrollLookupClass("");
          }}
          options={tradeOptions}
        />

        <CPDropdown
          label="Classification"
          value={payrollLookupClass}
          onChange={setPayrollLookupClass}
          options={classificationOptions}
        />
        </div>

        {payrollLookupTrade === "" ? (
          <p className="profile-value">Select a trade above.</p>
        ) : payrollLookupClass === "" ? (
          <p className="profile-value">Select a classification above.</p>
        ) : lookupRates.length === 0 ? (
          <p className="profile-value">No matrix rates for this trade and classification.</p>
        ) : isDesktop ? (
          <div style={{ marginTop: "16px", maxWidth: "560px" }}>
            <DeskTable
              caption="Matrix rates"
              rows={lookupRates}
              rowKey={(rate) =>
                `${rate.trade_id}-${rate.classification_name}-${rate.payroll_type}`
              }
              columns={[
                {
                  key: "type",
                  label: "Payroll type",
                  render: (rate) => (
                    <span className="dt-strong">{formatPayrollTypeLabel(rate.payroll_type)}</span>
                  ),
                },
                {
                  key: "rate",
                  label: "Hourly rate",
                  align: "right",
                  width: "160px",
                  render: (rate) => <span className="dt-num">{"$"}{rate.hourly_rate}</span>,
                },
              ]}
            />
          </div>
        ) : (
          lookupRates.map((rate) => (
            <div
              key={`${rate.trade_id}-${rate.classification_name}-${rate.payroll_type}`}
              className="profile-value"
              style={{ marginBottom: "10px" }}
            >
              {formatPayrollTypeLabel(rate.payroll_type)}
              {" | $"}
              {rate.hourly_rate}
            </div>
          ))
        )}
      </AdminFormCard>
    </div>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="PAYROLL RATE MANAGEMENT" onBack={onBack} />
        {content}
      </div>
    );
  }

  return content;
}
